/** @prettier */

import fscreen from 'fscreen';
import { useState, useCallback } from 'react';
import { useEventListener } from './useEventListener';

/**
 * Hook to allow enabling/disabling fullscreen for an Element and it's children.
 *
 * @param {HTMLElement} element In React this will probably be a ref, which can also
 * be created with the `useRef` hook
 */
export const useFullscreen = element => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const elementIsFullscreenElement = fscreen.fullscreenElement === element;
  const closeOtherFullscreen = async () => {
    // If something else is fullscreen, close it
    if (fscreen.fullscreenElement && !elementIsFullscreenElement) {
      await fscreen.exitFullscreen();
    }
  };

  const closeFullscreen = () => {
    if (isFullscreen && elementIsFullscreenElement) {
      fscreen.exitFullscreen();
    }
  };

  const openFullscreen = async () => {
    await closeOtherFullscreen();

    if (element) {
      fscreen.requestFullscreen(element);
    }
  };

  const setFullscreen = state => (state ? openFullscreen() : closeFullscreen());
  const toggleFullscreen = () => setFullscreen(!isFullscreen);

  const onFullscreenChange = useCallback(
    () => setIsFullscreen(fscreen.fullscreenElement === element),
    [element]
  );

  useEventListener(onFullscreenChange, 'fullscreenchange', fscreen);

  return {
    isFullscreen,
    toggleFullscreen,
  };
};
